"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary brand button using OKLCH design tokens
        default: "bg-[var(--color-brand-primary)] text-white hover-device:bg-[var(--color-brand-primary-hover)]",
        
        // CTA button variants using OKLCH design tokens  
        success: "bg-[var(--color-cta-success)] text-white hover-device:opacity-90",
        warning: "bg-[var(--color-cta-warning)] text-[var(--color-neutral-950)] hover-device:opacity-90", 
        danger: "bg-[var(--color-cta-danger)] text-white hover-device:opacity-90",
        accent: "bg-[var(--color-cta-accent)] text-white hover-device:opacity-90",
        
        // Secondary and outline variants
        secondary: "bg-transparent text-[var(--color-brand-primary)] border border-[var(--color-brand-primary)] hover-device:bg-[var(--color-hover-overlay)]",
        outline: "border border-[var(--color-neutral-300)] bg-white hover-device:bg-[var(--color-hover-overlay)] text-[var(--color-neutral-900)]",
        
        // Ghost and link variants  
        ghost: "hover-device:bg-[var(--color-hover-overlay)] text-[var(--color-neutral-900)]",
        link: "text-[var(--color-brand-primary)] underline-offset-4 hover-device:underline",
        
        // Legacy destructive for compatibility
        destructive: "bg-[var(--color-cta-danger)] text-white hover-device:opacity-90",
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-12 rounded-md px-8 text-base",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
